import React from 'react';
import { Box } from '@mui/material';
import { fadeIn, reducedMotion } from './authTokens';

/**
 * The ambient field behind the login screen: an earth limb rising in the
 * lower-left with city lights along its horizon, and diagonal light streaks in
 * the upper-right.
 *
 * Everything here is CSS gradients and blur. A photograph would cost 200-600KB
 * on the one route that must render before anything is cached, would need an
 * art-directed second asset for portrait, and could not follow the brand hues.
 *
 * Purely decorative, so the whole layer is `aria-hidden` and non-interactive.
 * Opacities are kept low deliberately: this sits under body copy, and the
 * moment a streak is visible enough to notice on its own it is too bright to
 * read across.
 */
const AuthBackdrop: React.FC = () => (
  <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {/* --- Star field. Four fixed gradients rather than generated elements:
        sub-pixel dots cost nothing as a background and never reflow. --- */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: 0.55,
        backgroundImage: [
          'radial-gradient(1.4px 1.4px at 18% 22%, rgba(255,255,255,0.7), transparent)',
          'radial-gradient(1.2px 1.2px at 62% 12%, rgba(255,255,255,0.55), transparent)',
          'radial-gradient(1.6px 1.6px at 84% 34%, rgba(190,215,255,0.6), transparent)',
          'radial-gradient(1.2px 1.2px at 38% 48%, rgba(255,255,255,0.4), transparent)',
          'radial-gradient(1.3px 1.3px at 74% 66%, rgba(255,255,255,0.35), transparent)',
          'radial-gradient(1.1px 1.1px at 28% 74%, rgba(200,220,255,0.4), transparent)',
        ].join(', '),
      }}
    />

    {/* --- Diagonal light streaks, upper-right. The mask is what keeps them
        from crossing the headline: they exist only in the top-right quadrant. --- */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'repeating-linear-gradient(118deg, rgba(160,190,255,0.085) 0px, rgba(160,190,255,0.085) 1px, transparent 1px, transparent 46px)',
        maskImage: 'radial-gradient(70% 62% at 96% -6%, #000 0%, transparent 72%)',
        WebkitMaskImage: 'radial-gradient(70% 62% at 96% -6%, #000 0%, transparent 72%)',
        animation: `${fadeIn} 1.6s ease .2s both`,
        ...reducedMotion,
      }}
    />
    {/* Two brighter individual streaks on top of the repeat, so the field has a
        focal accent instead of reading as uniform hatching. */}
    {[
      { top: '-16%', right: '6%', height: '62vh', opacity: 0.5 },
      { top: '-10%', right: '22%', height: '46vh', opacity: 0.32 },
    ].map((s, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: s.top,
          right: s.right,
          width: 2,
          height: s.height,
          transform: 'rotate(28deg)',
          transformOrigin: 'top center',
          background: 'linear-gradient(180deg, rgba(180,205,255,0.85) 0%, rgba(140,120,245,0.25) 45%, transparent 100%)',
          filter: 'blur(2px)',
          opacity: s.opacity,
          animation: `${fadeIn} 1.8s ease ${0.3 + i * 0.25}s both`,
          ...reducedMotion,
        }}
      />
    ))}

    {/* --- Earth limb, lower-left. A single oversized circle pushed mostly off
        the viewport, so only the horizon arc is ever on screen. --- */}
    <Box
      sx={{
        position: 'absolute',
        left: { xs: '-52%', md: '-24%' },
        bottom: { xs: '-74%', md: '-64%' },
        width: { xs: '150%', md: 'min(1320px, 108%)' },
        aspectRatio: '1 / 1',
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 50% 10%, rgba(92,142,255,0.20) 0%, rgba(11,20,48,0.94) 26%, #04081a 58%)',
        // The outer bloom is the atmosphere seen edge-on; without it the circle
        // reads as a cut-out hole rather than a body with air around it.
        boxShadow: '0 -30px 140px 12px rgba(86,140,255,0.20)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-2px',
          borderRadius: '50%',
          border: '1.5px solid rgba(132,178,255,0.42)',
          filter: 'blur(2.5px)',
        },
        // City lights: a warm wash along the upper arc plus three brighter
        // clusters, all blurred past the point of looking like geography.
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          backgroundImage: [
            'radial-gradient(58% 16% at 50% 0.5%, rgba(255,186,112,0.50) 0%, rgba(255,140,60,0.14) 46%, transparent 74%)',
            'radial-gradient(9% 5% at 34% 2.5%, rgba(255,205,140,0.55) 0%, transparent 70%)',
            'radial-gradient(7% 4% at 58% 1.5%, rgba(255,214,160,0.5) 0%, transparent 70%)',
            'radial-gradient(6% 3.5% at 71% 4%, rgba(255,190,120,0.42) 0%, transparent 70%)',
          ].join(', '),
          filter: 'blur(10px)',
        },
      }}
    />
  </Box>
);

export default AuthBackdrop;
